import axios, { AxiosInstance, AxiosResponse } from 'axios';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  membership_type: string;
  avatar_url?: string;
  is_admin: boolean;
  is_gym_owner: boolean;
  is_ceo: boolean;
  is_active: boolean;
  gym_id?: number;
  last_login?: string;
  created_at: string;
}

export interface Gym {
  id: number;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  description?: string;
  logo_url?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface GymStats {
  total_members: number;
  total_employees: number;
  total_users: number;
  active_workouts: number;
  today_classes: number;
  today_bookings: number;
}

export interface Group {
  id: number;
  gym_id: number;
  name: string;
  description?: string;
  color: string;
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
  created_by_first_name?: string;
  created_by_last_name?: string;
  members?: GroupMember[];
  programs?: GroupProgram[];
  member_count?: number;
  program_count?: number;
}

export interface GroupMember {
  id: number;
  group_id: number;
  user_id: number;
  added_by: number;
  joined_at: string;
  left_at?: string;
  is_active: boolean;
  notes?: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
  phone?: string;
  added_by_first_name: string;
  added_by_last_name: string;
}

export interface GroupProgram {
  id: number;
  group_id: number;
  program_id: number;
  assigned_by: number;
  assigned_at: string;
  removed_at?: string;
  is_active: boolean;
  notes?: string;
  program_name: string;
  program_description?: string;
  assigned_by_first_name: string;
  assigned_by_last_name: string;
}


export interface Trainer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  is_lead: boolean;
}

export interface WorkoutCategory {
  id: number;
  name: string;
  description: string;
  color: string;
  gym_id: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Workout {
  id: number;
  name: string;
  description?: string;
  instructions?: string;
  category_id?: number;
  gym_id: number;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  duration_minutes?: number;
  equipment_needed?: string[];
  muscle_groups?: string[];
  video_url?: string;
  image_url?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  category_name?: string;
  category_color?: string;
  type_name?: string;
}

export interface WorkoutProgram {
  id: number;
  name: string;
  description: string;
  gym_id: number;
  created_by: number;
  duration_weeks: number;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by_first_name: string;
  created_by_last_name: string;
  workouts?: Array<{
    id: number;
    program_id: number;
    workout_id: number;
    day_of_week: number;
    week_number: number;
    order_in_day: number;
    sets?: number;
    reps?: number;
    duration?: string;
    notes?: string;
    workout: {
      id: number;
      name: string;
      description: string;
      instructions?: string;
      duration_minutes?: number;
      equipment_needed?: string[];
      muscle_groups?: string[];
      video_url?: string;
      image_url?: string;
    };
  }>;
  supersets?: Array<{
    id: number;
    name: string;
    program_id: number;
    day_of_week: number;
    week_number: number;
    order_in_day: number;
    sets?: number;
    reps?: number;
    duration?: string;
    notes?: string;
    exercises?: Array<{
      id: number;
      superset_id: number;
      workout_id: number;
      order_in_superset: number;
      sets?: number;
      reps?: number;
      rest_seconds?: number;
      workout_name?: string;
      workout_description?: string;
      duration_minutes?: number;
      category_name?: string;
      category_color?: string;
    }>;
  }>;
}

export interface Superset {
  id: number;
  name: string;
  program_id: number;
  day_of_week: number;
  week_number: number;
  order_in_day: number;
  sets: number;
  reps: number;
  duration: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  exercises: SupersetExercise[];
}

export interface SupersetExercise {
  id: number;
  workout_id: number;
  workout_name: string;
  sets: number;
  reps: number;
  rest_seconds: number;
  order_in_superset: number;
}

export interface CalendarEvent {
  id: number;
  gym_id: number;
  title: string;
  description?: string;
  event_date: string;
  event_type: 'holiday' | 'reminder' | 'event';
  color: string;
  is_all_day: boolean;
  start_time?: string;
  end_time?: string;
  created_at: string;
  updated_at: string;
}

export interface Class {
  id: number;
  class_type_id: number;
  class_name: string;
  class_description?: string;
  location_id?: number;
  location_name?: string;
  gym_id: number;
  date: string;
  start_time: string;
  end_time: string;
  max_capacity: number;
  current_bookings: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  trainers: Trainer[];
  // Additional fields for recurring functionality
  isRecurring?: boolean;
  recurrenceType?: 'daily' | 'weekly' | 'custom';
  recurrenceDays?: string[];
  endDate?: string;
}

export interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  membership_type: string;
  bio?: string;
  avatar_url?: string;
  is_active: boolean;
  last_login?: string;
  created_at: string;
}

export interface GymRegistrationData {
  gym: {
    name: string;
    phone: string;
    email: string;
    logo_url?: string;
    address: string;
    description?: string;
  };
  owner: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    password: string;
  };
  setup: {
    workout_types: string[];
    class_types: string[];
  };
}

export interface EmployeeData {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  role: string;
  bio?: string;
}

// New types for enhanced admin functionality
export interface MemberNote {
  id: number;
  member_id: number;
  created_by: number;
  note: string;
  is_private: boolean;
  created_at: string;
  updated_at: string;
  created_by_name?: string;
}

export interface BillingCycle {
  id: number;
  name: string;
  duration_days: number;
  price: number;
  gym_id: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MemberBilling {
  id: number;
  member_id: number;
  billing_cycle_id: number;
  start_date: string;
  next_billing_date: string;
  amount: number;
  status: 'active' | 'paused' | 'cancelled';
  payment_method?: string;
  billing_notes?: string;
  created_at: string;
  updated_at: string;
  billing_cycle?: BillingCycle;
  member_name?: string;
}

export interface MemberProgramAssignment {
  id: number;
  member_id: number;
  program_id: number;
  assigned_by: number;
  start_date: string;
  status: 'active' | 'paused' | 'completed' | 'cancelled';
  notes?: string;
  assigned_at: string;
  updated_at: string;
  program_name: string;
  program_description?: string;
  duration_weeks: number;
  program_created_at: string;
}

export interface BillingTransaction {
  id: number;
  member_id: number;
  transaction_type: 'recurring' | 'one_time' | 'refund' | 'adjustment';
  amount: number;
  description: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  payment_method?: string;
  external_transaction_id?: string;
  due_date?: string;
  paid_date?: string;
  notes?: string;
  created_by: number;
  created_at: string;
  updated_at: string;
  member_name?: string;
  created_by_name?: string;
}

export interface CommunicationTemplate {
  id: number;
  name: string;
  type: 'email' | 'sms' | 'push_notification';
  subject?: string;
  content: string;
  variables: string[];
  gym_id: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CommunicationCampaign {
  id: number;
  name: string;
  type: 'email' | 'sms' | 'push_notification';
  template_id: number;
  custom_content?: string;
  target_audience: string;
  target_member_ids?: number[];
  status: 'draft' | 'scheduled' | 'sending' | 'completed' | 'cancelled';
  scheduled_at?: string;
  sent_at?: string;
  total_recipients: number;
  delivered_count: number;
  failed_count: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  template?: CommunicationTemplate;
  created_by_name?: string;
}

export interface GymSetting {
  id: number;
  gym_id: number;
  setting_key: string;
  setting_value?: string;
  setting_type: 'string' | 'number' | 'boolean' | 'json';
  description?: string;
  name: string;
  value?: string;
  unit?: string;
  created_at: string;
  updated_at: string;
}

export interface ChatMessage {
  id: number;
  sender_id: number;
  sender_name: string;
  sender_type: 'member' | 'employee';
  content: string;
  message_type: 'text' | 'image' | 'file';
  file_url?: string;
  is_read: boolean;
  read_at?: string;
  timestamp: string;
}

export interface ChatConversation {
  id: number;
  member_id: number;
  member_name: string;
  member_avatar?: string;
  last_message: string;
  last_message_time: string;
  unread_count: number;
  is_online: boolean;
}

// API Service Class
class ApiService {
  private api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 5000, // 5 second timeout for faster error responses
    });

    // Load token from localStorage
    this.token = localStorage.getItem('token');
    if (this.token) {
      this.setAuthToken(this.token);
    }

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          this.clearAuthToken();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication methods
  setAuthToken(token: string) {
    this.token = token;
    localStorage.setItem('token', token);
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  clearAuthToken() {
    this.token = null;
    localStorage.removeItem('token');
    delete this.api.defaults.headers.common['Authorization'];
  }

  // Auth endpoints
  async login(credentials: LoginCredentials): Promise<{ token: string; user: User }> {
    const response: AxiosResponse<{ token: string; user: User }> = await this.api.post('/api/auth/login', credentials);
    this.setAuthToken(response.data.token);
    return response.data;
  }

  async loginWithTakeoverToken(token: string): Promise<{ token: string; user: User; gym: any }> {
    const response: AxiosResponse<{ token: string; user: User; gym: any }> = await this.api.post(`/api/auth/login?takeover_token=${token}`);
    this.setAuthToken(response.data.token);
    return response.data;
  }

  async verifyToken(): Promise<{ user: User }> {
    const response: AxiosResponse<{ user: User }> = await this.api.get('/api/auth/verify');
    return response.data;
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.post('/api/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, password: string, confirmPassword: string): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.post('/api/auth/reset-password', { token, password, confirmPassword });
    return response.data;
  }

  // Google OAuth endpoints
  async getGoogleSSOUrl(): Promise<{ authUrl: string }> {
    const response: AxiosResponse<{ authUrl: string }> = await this.api.get('/api/auth/google/sso');
    return response.data;
  }

  async getGoogleEmailConnectUrl(): Promise<{ authUrl: string }> {
    const response: AxiosResponse<{ authUrl: string }> = await this.api.get('/api/auth/google/connect-email');
    return response.data;
  }

  async getGoogleConnectionStatus(): Promise<{ connected: boolean; email: string | null }> {
    const response: AxiosResponse<{ connected: boolean; email: string | null }> = await this.api.get('/api/auth/google/status');
    return response.data;
  }

  async disconnectGoogleAccount(): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.api.post('/api/auth/google/disconnect');
    return response.data;
  }

  // Gym endpoints
  async getGymInfo(): Promise<Gym> {
    const response: AxiosResponse<Gym> = await this.api.get('/api/gyms/me');
    return response.data;
  }

  async updateGym(gymData: Partial<Gym>): Promise<Gym> {
    const response: AxiosResponse<Gym> = await this.api.put('/api/gyms/me', gymData);
    return response.data;
  }

  async updateGymInfo(gymData: {
    name: string;
    address?: string;
    phone?: string;
    email?: string;
    description?: string;
    logo_url?: string;
  }): Promise<Gym> {
    const response: AxiosResponse<Gym> = await this.api.put('/api/gyms/me', gymData);
    return response.data;
  }

  async updateGymSetting(settingId: number, value: string): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.put(`/api/communication/settings/${settingId}`, { value });
    return response.data;
  }

  async uploadGymLogo(formData: FormData): Promise<{ logo_url: string }> {
    const response: AxiosResponse<{ logo_url: string }> = await this.api.post('/api/gyms/upload-logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getGymStats(): Promise<GymStats> {
    const response: AxiosResponse<GymStats> = await this.api.get('/api/gyms/stats');
    return response.data;
  }

  async registerGym(data: GymRegistrationData): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.api.post('/api/gyms/register', data);
    return response.data;
  }

  // CEO endpoints
  async enrollGymOld(data: any): Promise<{ success: boolean; message: string; gym: any; owner: any }> {
    const response: AxiosResponse<{ success: boolean; message: string; gym: any; owner: any }> = await this.api.post('/api/ceo/enroll-gym', data);
    return response.data;
  }

  async getEnrolledGyms(): Promise<any[]> {
    const response: AxiosResponse<any[]> = await this.api.get('/api/ceo/gyms');
    return response.data;
  }

  async getGymDetails(gymId: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/ceo/gyms/${gymId}`);
    return response.data;
  }

  // User endpoints
  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<User> = await this.api.get('/api/users/me');
    return response.data;
  }

  async updateCurrentUser(userData: Partial<User>): Promise<User> {
    const response: AxiosResponse<User> = await this.api.put('/api/users/me', userData);
    return response.data;
  }

  async getEmployees(): Promise<Employee[]> {
    const response: AxiosResponse<Employee[]> = await this.api.get('/api/users/employees');
    return response.data;
  }

  async addEmployee(employeeData: EmployeeData): Promise<{ success: boolean; message: string; employee: Employee }> {
    const response: AxiosResponse<{ success: boolean; message: string; employee: Employee }> = await this.api.post('/api/users/employees', employeeData);
    return response.data;
  }

  async toggleEmployeeStatus(employeeId: number, action: 'activate' | 'deactivate'): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.api.put(`/api/users/${employeeId}/${action}`);
    return response.data;
  }

  async updateEmployee(employeeId: number, employeeData: Partial<EmployeeData>): Promise<{ success: boolean; message: string; employee: Employee }> {
    const response: AxiosResponse<{ success: boolean; message: string; employee: Employee }> = await this.api.put(`/api/users/employees/${employeeId}`, employeeData);
    return response.data;
  }

  // Workout endpoints
  async getWorkouts(): Promise<Workout[]> {
    const response: AxiosResponse<Workout[]> = await this.api.get('/api/workouts');
    return response.data;
  }

  async createWorkout(data: {
    name: string;
    description?: string;
    instructions?: string;
    category_id?: number;
    difficulty?: 'beginner' | 'intermediate' | 'advanced';
    duration_minutes?: number;
    equipment_needed?: string[];
    muscle_groups?: string[];
    video_url?: string;
    image_url?: string;
  }): Promise<Workout> {
    const response: AxiosResponse<Workout> = await this.api.post('/api/workouts', data);
    return response.data;
  }

  async updateWorkout(id: number, data: {
    name: string;
    description?: string;
    instructions?: string;
    category_id?: number;
    duration_minutes?: number;
    equipment_needed?: string[];
    muscle_groups?: string[];
    video_url?: string;
    image_url?: string;
  }): Promise<Workout> {
    const response: AxiosResponse<Workout> = await this.api.put(`/api/workouts/${id}`, data);
    return response.data;
  }

  async deleteWorkout(id: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/api/workouts/${id}`);
    return response.data;
  }

  async getWorkoutCategories(): Promise<WorkoutCategory[]> {
    console.log('🔍 API: Getting workout categories...');
    const response: AxiosResponse<WorkoutCategory[]> = await this.api.get('/api/workouts/categories');
    console.log('🔍 API: Categories response:', response.data);
    console.log('🔍 API: Response status:', response.status);
    return response.data;
  }

  async createWorkoutCategory(data: {
    name: string;
    description?: string;
    color?: string;
  }): Promise<WorkoutCategory> {
    const response: AxiosResponse<WorkoutCategory> = await this.api.post('/api/workouts/categories', data);
    return response.data;
  }

  async deleteWorkoutCategory(id: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/api/workouts/categories/${id}`);
    return response.data;
  }

  async updateWorkoutCategory(id: number, data: {
    name: string;
    color: string;
  }): Promise<WorkoutCategory> {
    const response: AxiosResponse<WorkoutCategory> = await this.api.put(`/api/workouts/categories/${id}`, data);
    return response.data;
  }

  async getWorkoutPrograms(): Promise<WorkoutProgram[]> {
    const response: AxiosResponse<WorkoutProgram[]> = await this.api.get('/api/workouts/programs');
    return response.data;
  }

  async getWorkoutProgram(id: number): Promise<WorkoutProgram> {
    const response: AxiosResponse<WorkoutProgram> = await this.api.get(`/api/workouts/programs/${id}`);
    return response.data;
  }

  async updateWorkoutProgram(id: number, data: {
    name: string;
    description?: string;
    duration_weeks: number;
    workouts: Array<{
      workout_id: number;
      day_of_week: number;
      week_number: number;
      order_in_day?: number;
      sets?: number;
      reps?: number;
      duration?: string;
      notes?: string;
    }>;
  }): Promise<WorkoutProgram> {
    const response: AxiosResponse<WorkoutProgram> = await this.api.put(`/api/workouts/programs/${id}`, data);
    return response.data;
  }

  async createWorkoutProgram(data: {
    name: string;
    description?: string;
    duration_weeks: number;
    workouts: Array<{
      workout_id: number;
      day_of_week: number;
      week_number: number;
      order_in_day?: number;
      sets?: number;
      reps?: number;
      duration?: string;
      notes?: string;
    }>;
  }): Promise<WorkoutProgram> {
    const response: AxiosResponse<WorkoutProgram> = await this.api.post('/api/workouts/programs', data);
    return response.data;
  }

  async deleteWorkoutProgram(id: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/api/workouts/programs/${id}`);
    return response.data;
  }

  // Class endpoints
  async getClasses(date?: string): Promise<Class[]> {
    const params = date ? { date } : {};
    const response: AxiosResponse<Class[]> = await this.api.get('/api/classes', { params });
    return response.data;
  }

  async getClassById(id: number): Promise<Class> {
    const response: AxiosResponse<Class> = await this.api.get(`/api/classes/${id}`);
    return response.data;
  }

  async createClass(classData: {
    class_type_id: number;
    instructor_ids?: number[];
    location_id?: number;
    date: string;
    start_time: string;
    end_time: string;
    max_capacity: number;
    isRecurring?: boolean;
    recurrenceType?: 'daily' | 'weekly' | 'custom';
    recurrenceDays?: string[];
    endDate?: string;
  }): Promise<Class> {
    const response: AxiosResponse<Class> = await this.api.post('/api/classes', classData);
    return response.data;
  }

  async updateClass(id: number, classData: {
    class_type_id: number;
    instructor_ids?: number[];
    location_id?: number;
    date: string;
    start_time: string;
    end_time: string;
    max_capacity: number;
    isRecurring?: boolean;
    recurrenceType?: 'daily' | 'weekly' | 'custom';
    recurrenceDays?: string[];
    endDate?: string;
  }): Promise<Class> {
    const response: AxiosResponse<Class> = await this.api.put(`/api/classes/${id}`, classData);
    return response.data;
  }

  async deleteClass(id: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/api/classes/${id}`);
    return response.data;
  }

  async getClassBookings(classId: number): Promise<Array<{ id: number; member_name: string; member_email: string; booking_date: string }>> {
    // TODO: Implement API endpoint for getting class bookings
    // const response: AxiosResponse<Array<{ id: number; member_name: string; member_email: string; booking_date: string }>> = await this.api.get(`/api/classes/${classId}/bookings`);
    // return response.data;
    throw new Error('API endpoint not yet implemented');
  }

  async getClassTypes(): Promise<{ id: number; name: string; description?: string }[]> {
    const response: AxiosResponse<{ id: number; name: string; description?: string }[]> = await this.api.get('/api/admin/class-types');
    return response.data;
  }

  async getInstructors(): Promise<{ id: number; first_name: string; last_name: string; email: string; phone?: string; bio?: string }[]> {
    const response: AxiosResponse<{ id: number; first_name: string; last_name: string; email: string; phone?: string; bio?: string }[]> = await this.api.get('/api/classes/instructors');
    return response.data;
  }

  async createClassType(classTypeData: {
    name: string;
    description?: string;
  }): Promise<{ id: number; name: string; description?: string }> {
    const response: AxiosResponse<{ id: number; name: string; description?: string }> = await this.api.post('/api/admin/class-types', classTypeData);
    return response.data;
  }

  async getLocations(): Promise<{ id: number; name: string; address: string }[]> {
    const response: AxiosResponse<{ id: number; name: string; address: string }[]> = await this.api.get('/api/locations');
    return response.data;
  }

  async createLocation(locationData: {
    name: string;
    address: string;
  }): Promise<{ id: number; name: string; address: string }> {
    const response: AxiosResponse<{ id: number; name: string; address: string }> = await this.api.post('/api/locations', locationData);
    return response.data;
  }

  async updateClassType(id: number, classTypeData: {
    name: string;
    description?: string;
  }): Promise<{ id: number; name: string; description?: string }> {
    const response: AxiosResponse<{ id: number; name: string; description?: string }> = await this.api.put(`/api/admin/class-types/${id}`, classTypeData);
    return response.data;
  }

  async deleteClassType(id: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/api/admin/class-types/${id}`);
    return response.data;
  }

  async updateLocation(id: number, locationData: {
    name: string;
    address: string;
  }): Promise<{ id: number; name: string; address: string }> {
    const response: AxiosResponse<{ id: number; name: string; address: string }> = await this.api.put(`/api/locations/${id}`, locationData);
    return response.data;
  }

  async deleteLocation(id: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/api/locations/${id}`);
    return response.data;
  }

  // Calendar Events endpoints
  async getCalendarEvents(startDate?: string, endDate?: string): Promise<CalendarEvent[]> {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response: AxiosResponse<CalendarEvent[]> = await this.api.get('/api/calendar-events', { params });
    return response.data;
  }

  async getCalendarEvent(id: number): Promise<CalendarEvent> {
    const response: AxiosResponse<CalendarEvent> = await this.api.get(`/api/calendar-events/${id}`);
    return response.data;
  }

  async createCalendarEvent(eventData: {
    title: string;
    description?: string;
    event_date: string;
    event_type?: 'holiday' | 'reminder' | 'event';
    color?: string;
    is_all_day?: boolean;
    start_time?: string;
    end_time?: string;
  }): Promise<CalendarEvent> {
    const response: AxiosResponse<CalendarEvent> = await this.api.post('/api/calendar-events', eventData);
    return response.data;
  }

  async updateCalendarEvent(id: number, eventData: {
    title?: string;
    description?: string;
    event_date?: string;
    event_type?: 'holiday' | 'reminder' | 'event';
    color?: string;
    is_all_day?: boolean;
    start_time?: string;
    end_time?: string;
  }): Promise<CalendarEvent> {
    const response: AxiosResponse<CalendarEvent> = await this.api.put(`/api/calendar-events/${id}`, eventData);
    return response.data;
  }

  async deleteCalendarEvent(id: number): Promise<void> {
    await this.api.delete(`/api/calendar-events/${id}`);
  }

  // Admin endpoints
  async getAdminDashboard(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/admin/dashboard');
    return response.data;
  }

  // Members management endpoints
  async getMembers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }): Promise<{
    members: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const response: AxiosResponse<{
      members: any[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      };
    }> = await this.api.get('/api/members', { params });
    return response.data;
  }

  async getMemberDetails(memberId: number): Promise<{
    member: any;
    billing: MemberBilling | null;
    transactions: BillingTransaction[];
    notes: MemberNote[];
  }> {
    const response: AxiosResponse<{
      member: any;
      billing: MemberBilling | null;
      transactions: BillingTransaction[];
      notes: MemberNote[];
    }> = await this.api.get(`/api/members/${memberId}`);
    return response.data;
  }

  async addMemberNote(memberId: number, noteData: {
    note: string;
    is_private: boolean;
  }): Promise<MemberNote> {
    const response: AxiosResponse<MemberNote> = await this.api.post(`/api/members/${memberId}/notes`, noteData);
    return response.data;
  }

  async updateMemberBilling(memberId: number, billingData: {
    billing_cycle_id: number;
    start_date: string;
    amount: number;
    payment_method?: string;
    billing_notes?: string;
  }): Promise<MemberBilling> {
    const response: AxiosResponse<MemberBilling> = await this.api.put(`/api/members/${memberId}/billing`, billingData);
    return response.data;
  }

  async createBillingTransaction(memberId: number, transactionData: {
    transaction_type: 'recurring' | 'one_time' | 'refund' | 'adjustment';
    amount: number;
    description: string;
    payment_method?: string;
    due_date?: string;
    notes?: string;
  }): Promise<BillingTransaction> {
    const response: AxiosResponse<BillingTransaction> = await this.api.post(`/api/members/${memberId}/transactions`, transactionData);
    return response.data;
  }

  async getBillingCycles(): Promise<BillingCycle[]> {
    const response: AxiosResponse<BillingCycle[]> = await this.api.get('/api/members/billing/cycles');
    return response.data;
  }

  async createBillingCycle(cycleData: {
    name: string;
    duration_days: number;
    price: number;
    is_active?: boolean;
  }): Promise<BillingCycle> {
    const response: AxiosResponse<BillingCycle> = await this.api.post('/api/members/billing/cycles', cycleData);
    return response.data;
  }

  async updateMemberStatus(memberId: number, isActive: boolean): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.put(`/api/members/${memberId}/status`, { is_active: isActive });
    return response.data;
  }

  // Communication endpoints
  async getCommunicationTemplates(): Promise<CommunicationTemplate[]> {
    const response: AxiosResponse<CommunicationTemplate[]> = await this.api.get('/api/communication/templates');
    return response.data;
  }

  async createCommunicationTemplate(templateData: {
    name: string;
    type: 'email' | 'sms' | 'push_notification';
    subject?: string;
    content: string;
    variables?: string[];
    is_active?: boolean;
  }): Promise<CommunicationTemplate> {
    const response: AxiosResponse<CommunicationTemplate> = await this.api.post('/api/communication/templates', templateData);
    return response.data;
  }

  async updateCommunicationTemplate(templateId: number, templateData: {
    name: string;
    type: 'email' | 'sms' | 'push_notification';
    subject?: string;
    content: string;
    variables?: string[];
    is_active?: boolean;
  }): Promise<CommunicationTemplate> {
    const response: AxiosResponse<CommunicationTemplate> = await this.api.put(`/api/communication/templates/${templateId}`, templateData);
    return response.data;
  }

  async deleteCommunicationTemplate(templateId: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/api/communication/templates/${templateId}`);
    return response.data;
  }

  async getCommunicationCampaigns(): Promise<CommunicationCampaign[]> {
    const response: AxiosResponse<CommunicationCampaign[]> = await this.api.get('/api/communication/campaigns');
    return response.data;
  }

  async createCommunicationCampaign(campaignData: {
    name: string;
    type: 'email' | 'sms' | 'push_notification';
    template_id?: number;
    custom_content?: string;
    target_audience: 'all_members' | 'specific_members' | 'employees' | 'inactive_members';
    target_member_ids?: number[];
    scheduled_at?: string;
  }): Promise<CommunicationCampaign> {
    const response: AxiosResponse<CommunicationCampaign> = await this.api.post('/api/communication/campaigns', campaignData);
    return response.data;
  }

  async sendCommunicationCampaign(campaignId: number): Promise<{
    message: string;
    recipients_count: number;
  }> {
    const response: AxiosResponse<{
      message: string;
      recipients_count: number;
    }> = await this.api.post(`/api/communication/campaigns/${campaignId}/send`);
    return response.data;
  }


  async updateCommunicationCampaign(campaignId: number, campaignData: {
    name: string;
    type: 'email' | 'sms' | 'push_notification';
    template_id?: number;
    custom_content?: string;
    target_audience: 'all_members' | 'specific_members' | 'employees' | 'inactive_members';
    target_member_ids?: number[];
    scheduled_at?: string;
  }): Promise<CommunicationCampaign> {
    const response: AxiosResponse<CommunicationCampaign> = await this.api.put(`/api/communication/campaigns/${campaignId}`, campaignData);
    return response.data;
  }

  async deleteCommunicationCampaign(campaignId: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/api/communication/campaigns/${campaignId}`);
    return response.data;
  }

  // Send individual message (member or non-member)
  async sendIndividualMessage(messageData: {
    recipient_type: 'member' | 'non_member';
    member_id?: number;
    email?: string;
    phone?: string;
    type: 'email' | 'sms' | 'push_notification';
    subject?: string;
    content: string;
  }): Promise<{ message: string; conversation_id?: number; recipient_type: string; type: string }> {
    const response: AxiosResponse<{ message: string; conversation_id?: number; recipient_type: string; type: string }> = await this.api.post('/api/communication/send-individual', messageData);
    return response.data;
  }

  // Get all conversations
  async getConversations(): Promise<any[]> {
    const response: AxiosResponse<any[]> = await this.api.get('/api/communication/conversations');
    return response.data;
  }

  // Get messages for a conversation
  async getConversationMessages(conversationId: number): Promise<any[]> {
    const response: AxiosResponse<any[]> = await this.api.get(`/api/communication/conversations/${conversationId}/messages`);
    return response.data;
  }

  // Reply to a conversation
  async replyToConversation(conversationId: number, data: {
    type: 'email' | 'sms' | 'push_notification';
    content: string;
    subject?: string;
  }): Promise<{ message: string; conversation_id: number }> {
    const response: AxiosResponse<{ message: string; conversation_id: number }> = await this.api.post(`/api/communication/conversations/${conversationId}/reply`, data);
    return response.data;
  }

  // Send bulk messages
  async sendBulkMessage(data: {
    type: 'email' | 'sms' | 'push_notification';
    content: string;
    subject?: string;
    recipient_type: 'all_members' | 'group' | 'selected';
    member_ids?: number[];
    group_id?: number;
  }): Promise<{ message: string; sent: number; failed: number; errors: string[] }> {
    const response: AxiosResponse<{ message: string; sent: number; failed: number; errors: string[] }> = await this.api.post('/api/communication/send-bulk', data);
    return response.data;
  }

  async getCampaignLogs(campaignId: number): Promise<any[]> {
    const response: AxiosResponse<any[]> = await this.api.get(`/api/communication/campaigns/${campaignId}/logs`);
    return response.data;
  }

  // Gym settings endpoints
  async getGymSettings(): Promise<GymSetting[]> {
    const response: AxiosResponse<GymSetting[]> = await this.api.get('/api/communication/settings');
    return response.data;
  }

  async updateGymSettings(settings: GymSetting[]): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.put('/api/communication/settings', settings);
    return response.data;
  }

  async getGymBranding(): Promise<{
    gym: any;
    branding: GymSetting[];
  }> {
    const response: AxiosResponse<{
      gym: any;
      branding: GymSetting[];
    }> = await this.api.get('/api/communication/branding');
    return response.data;
  }

  async updateGymBranding(brandingData: {
    gym?: any;
    branding?: GymSetting[];
  }): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.put('/api/communication/branding', brandingData);
    return response.data;
  }

  // Chat API methods
  async getChatConversations(): Promise<ChatConversation[]> {
    const response: AxiosResponse<ChatConversation[]> = await this.api.get('/api/chat/conversations');
    return response.data;
  }

  async getChatMessages(conversationId: number): Promise<ChatMessage[]> {
    const response: AxiosResponse<ChatMessage[]> = await this.api.get(`/api/chat/conversations/${conversationId}/messages`);
    return response.data;
  }

  async createChatConversation(memberId: number): Promise<{ id: number }> {
    const response: AxiosResponse<{ id: number }> = await this.api.post('/api/chat/conversations', { member_id: memberId });
    return response.data;
  }

  async sendChatMessage(messageData: {
    conversation_id: number;
    content: string;
    message_type?: 'text' | 'image' | 'file';
    file_url?: string;
  }): Promise<ChatMessage> {
    const response: AxiosResponse<ChatMessage> = await this.api.post('/api/chat/messages', messageData);
    return response.data;
  }

  async markChatMessagesAsRead(messageIds: number[]): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.put('/api/chat/messages/read', { message_ids: messageIds });
    return response.data;
  }

  async getChatUnreadCount(): Promise<{ unread_count: number }> {
    const response: AxiosResponse<{ unread_count: number }> = await this.api.get('/api/chat/unread-count');
    return response.data;
  }




  async getMemberProgress(memberId: number, programId?: number): Promise<{
    program_id: number;
    program_name: string;
    total_workouts: number;
    completed_workouts: number;
    progress_percentage: number;
    last_workout_date?: string;
    upcoming_workouts: Array<{
      workout_id: number;
      workout_name: string;
      scheduled_date: string;
      status: 'pending' | 'completed' | 'skipped';
    }>;
  }> {
    const url = programId 
      ? `/api/members/${memberId}/progress?program_id=${programId}`
      : `/api/members/${memberId}/progress`;
    const response: AxiosResponse<{
      program_id: number;
      program_name: string;
      total_workouts: number;
      completed_workouts: number;
      progress_percentage: number;
      last_workout_date?: string;
      upcoming_workouts: Array<{
        workout_id: number;
        workout_name: string;
        scheduled_date: string;
        status: 'pending' | 'completed' | 'skipped';
      }>;
    }> = await this.api.get(url);
    return response.data;
  }

  // Member request endpoints
  async getMemberRequests(): Promise<Array<{
    id: number;
    user_id: number;
    gym_id: number;
    status: 'pending' | 'approved' | 'rejected';
    requested_at: string;
    responded_at?: string;
    responded_by?: number;
    notes?: string;
    user: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
      phone?: string;
    };
    gym: {
      id: number;
      name: string;
    };
  }>> {
    const response: AxiosResponse<Array<{
      id: number;
      user_id: number;
      gym_id: number;
      status: 'pending' | 'approved' | 'rejected';
      requested_at: string;
      responded_at?: string;
      responded_by?: number;
      notes?: string;
      user: {
        id: number;
        first_name: string;
        last_name: string;
        email: string;
        phone?: string;
      };
      gym: {
        id: number;
        name: string;
      };
    }>> = await this.api.get('/api/members/requests');
    return response.data;
  }

  async getMemberRequestHistory(): Promise<Array<{
    id: number;
    user_id: number;
    gym_id: number;
    status: 'approved' | 'rejected';
    requested_at: string;
    responded_at?: string;
    responded_by?: number;
    notes?: string;
    user: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
      phone?: string;
      is_active: boolean;
    };
    gym: {
      id: number;
      name: string;
    };
    responder?: {
      first_name: string;
      last_name: string;
    };
  }>> {
    const response: AxiosResponse<Array<{
      id: number;
      user_id: number;
      gym_id: number;
      status: 'approved' | 'rejected';
      requested_at: string;
      responded_at?: string;
      responded_by?: number;
      notes?: string;
      user: {
        id: number;
        first_name: string;
        last_name: string;
        email: string;
        phone?: string;
        is_active: boolean;
      };
      gym: {
        id: number;
        name: string;
      };
      responder?: {
        first_name: string;
        last_name: string;
      };
    }>> = await this.api.get('/api/members/requests/history');
    return response.data;
  }

  async updateMemberRequest(requestId: number, status: 'approved' | 'rejected', notes?: string, membership_type_id?: number | null): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.put(`/api/members/requests/${requestId}`, {
      status,
      notes,
      membership_type_id
    });
    return response.data;
  }

  async updateMemberMembershipType(memberId: number, membership_type_id: number | null): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.put(`/api/members/${memberId}/membership-type`, {
      membership_type_id
    });
    return response.data;
  }

  async deleteMember(memberId: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/api/members/${memberId}`);
    return response.data;
  }

  // Groups API methods
  async getGroups(includeMembers = false, includePrograms = false): Promise<{ groups: Group[] }> {
    const response: AxiosResponse<{ groups: Group[] }> = await this.api.get('/api/groups', {
      params: {
        include_members: includeMembers.toString(),
        include_programs: includePrograms.toString()
      }
    });
    return response.data;
  }

  async getGroup(groupId: number): Promise<{ group: Group }> {
    const response: AxiosResponse<{ group: Group }> = await this.api.get(`/api/groups/${groupId}`);
    return response.data;
  }

  async createGroup(groupData: {
    name: string;
    description?: string;
    color?: string;
  }): Promise<{ message: string; group: Group }> {
    const response: AxiosResponse<{ message: string; group: Group }> = await this.api.post('/api/groups', groupData);
    return response.data;
  }

  async updateGroup(groupId: number, groupData: {
    name?: string;
    description?: string;
    color?: string;
  }): Promise<{ message: string; group: Group }> {
    const response: AxiosResponse<{ message: string; group: Group }> = await this.api.put(`/api/groups/${groupId}`, groupData);
    return response.data;
  }

  async deleteGroup(groupId: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/api/groups/${groupId}`);
    return response.data;
  }

  async addMemberToGroup(groupId: number, memberData: {
    user_id: number;
    notes?: string;
  }): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.post(`/api/groups/${groupId}/members`, memberData);
    return response.data;
  }

  async removeMemberFromGroup(groupId: number, userId: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/api/groups/${groupId}/members/${userId}`);
    return response.data;
  }

  async assignProgramToGroup(groupId: number, programData: {
    program_id: number;
    notes?: string;
  }): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.post(`/api/groups/${groupId}/programs`, programData);
    return response.data;
  }

  async removeProgramFromGroup(groupId: number, programId: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/api/groups/${groupId}/programs/${programId}`);
    return response.data;
  }

  // Member Programs
  async getMemberPrograms(memberId: number): Promise<{
    active_programs: Array<{
      id: number;
      program_id: number;
      program_name: string;
      start_date: string;
      end_date: string;
      status: 'active' | 'completed' | 'paused' | 'cancelled';
      progress_percentage: number;
    }>;
    available_programs: WorkoutProgram[];
  }> {
    const response: AxiosResponse<{
      active_programs: Array<{
        id: number;
        program_id: number;
        program_name: string;
        start_date: string;
        end_date: string;
        status: 'active' | 'completed' | 'paused' | 'cancelled';
        progress_percentage: number;
      }>;
      available_programs: WorkoutProgram[];
    }> = await this.api.get(`/api/members/${memberId}/programs`);
    return response.data;
  }

  async assignProgramToMember(memberId: number, programData: {
    program_id: number;
    start_date?: string;
    notes?: string;
  }): Promise<MemberProgramAssignment> {
    const response: AxiosResponse<MemberProgramAssignment> = await this.api.post(`/api/members/${memberId}/programs`, programData);
    return response.data;
  }

  async updateMemberProgram(memberId: number, assignmentId: number, updateData: {
    status?: 'active' | 'paused' | 'completed' | 'cancelled';
    notes?: string;
  }): Promise<MemberProgramAssignment> {
    const response: AxiosResponse<MemberProgramAssignment> = await this.api.put(`/api/members/${memberId}/programs/${assignmentId}`, updateData);
    return response.data;
  }

  async removeProgramFromMember(memberId: number, assignmentId: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/api/members/${memberId}/programs/${assignmentId}`);
    return response.data;
  }

  // Superset API methods
  async getSupersetsForProgram(programId: number): Promise<Superset[]> {
    const response: AxiosResponse<Superset[]> = await this.api.get(`/api/supersets/programs/${programId}/supersets`);
    return response.data;
  }

  async createSuperset(data: {
    name: string;
    program_id: number;
    day_of_week: number;
    week_number?: number;
    order_in_day?: number;
    sets?: number;
    reps?: number;
    duration?: string;
    notes?: string;
    exercises: Array<{
      workout_id: number;
      sets: number;
      reps: number;
      rest_seconds: number;
    }>;
  }): Promise<Superset> {
    const response: AxiosResponse<Superset> = await this.api.post('/api/supersets', data);
    return response.data;
  }

  async updateSuperset(id: number, data: {
    name?: string;
    day_of_week?: number;
    week_number?: number;
    order_in_day?: number;
    sets?: number;
    reps?: number;
    duration?: string;
    notes?: string;
    exercises?: Array<{
      workout_id: number;
      sets: number;
      reps: number;
      rest_seconds: number;
    }>;
  }): Promise<Superset> {
    const response: AxiosResponse<Superset> = await this.api.put(`/api/supersets/${id}`, data);
    return response.data;
  }

  async deleteSuperset(id: number): Promise<void> {
    await this.api.delete(`/api/supersets/${id}`);
  }

  // Reports endpoints
  async getMemberGrowth(): Promise<Array<{ date: string; count: number }>> {
    const response: AxiosResponse<Array<{ date: string; count: number }>> = await this.api.get('/api/reports/members/growth');
    return response.data;
  }

  async getMemberBreakdown(): Promise<Array<{ type: string; count: number }>> {
    const response: AxiosResponse<Array<{ type: string; count: number }>> = await this.api.get('/api/reports/members/breakdown');
    return response.data;
  }

  async getClassAttendance(): Promise<Array<{ date: string; attendance: number }>> {
    const response: AxiosResponse<Array<{ date: string; attendance: number }>> = await this.api.get('/api/reports/classes/attendance');
    return response.data;
  }

  async getWorkoutCompletion(): Promise<Array<{ date: string; completions: number }>> {
    const response: AxiosResponse<Array<{ date: string; completions: number }>> = await this.api.get('/api/reports/workouts/completion');
    return response.data;
  }

  async getPeakHours(): Promise<Array<{ hour: number; bookings: number }>> {
    const response: AxiosResponse<Array<{ hour: number; bookings: number }>> = await this.api.get('/api/reports/classes/peak-hours');
    return response.data;
  }

  async getComprehensiveReport(startDate?: string, endDate?: string): Promise<{
    period: { start: string; end: string };
    metrics: {
      total_members: number;
      active_members: number;
      inactive_members: number;
      total_classes: number;
      total_bookings: number;
      total_workout_completions: number;
      new_members: number;
    };
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const url = `/api/reports/comprehensive${params.toString() ? '?' + params.toString() : ''}`;
    const response: AxiosResponse<{
      period: { start: string; end: string };
      metrics: {
        total_members: number;
        active_members: number;
        inactive_members: number;
        total_classes: number;
        total_bookings: number;
        total_workout_completions: number;
        new_members: number;
      };
    }> = await this.api.get(url);
    return response.data;
  }

  // Communication reports endpoints
  async getCommunicationCampaignsReport(): Promise<Array<{ date: string; count: number }>> {
    const response: AxiosResponse<Array<{ date: string; count: number }>> = await this.api.get('/api/reports/communication/campaigns');
    return response.data;
  }

  async getCommunicationDelivery(): Promise<Record<string, Record<string, number>>> {
    const response: AxiosResponse<Record<string, Record<string, number>>> = await this.api.get('/api/reports/communication/delivery');
    return response.data;
  }

  async getCommunicationEngagement(): Promise<{
    total_sent: number;
    total_delivered: number;
    total_failed: number;
    total_opened: number;
    total_clicked: number;
    open_rate: number;
    click_rate: number;
    delivery_rate: number;
  }> {
    const response: AxiosResponse<{
      total_sent: number;
      total_delivered: number;
      total_failed: number;
      total_opened: number;
      total_clicked: number;
      open_rate: number;
      click_rate: number;
      delivery_rate: number;
    }> = await this.api.get('/api/reports/communication/engagement');
    return response.data;
  }

  async getCommunicationTypeBreakdown(): Promise<Array<{ type: string; count: number }>> {
    const response: AxiosResponse<Array<{ type: string; count: number }>> = await this.api.get('/api/reports/communication/type-breakdown');
    return response.data;
  }

  // ==================== AUTOMATION WORKFLOWS ====================
  
  async getAutomationWorkflows(): Promise<any[]> {
    const response: AxiosResponse<any[]> = await this.api.get('/api/communication/automation/workflows');
    return response.data;
  }

  async getAutomationWorkflow(id: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/communication/automation/workflows/${id}`);
    return response.data;
  }

  async createAutomationWorkflow(workflowData: {
    name: string;
    description?: string;
    trigger_type: 'member_signup' | 'member_inactive' | 'workout_completed' | 'class_booked' | 'class_missed' | 'custom';
    trigger_config?: Record<string, any>;
    steps: Array<{
      step_order: number;
      action_type: 'send_email' | 'send_sms' | 'send_push' | 'wait' | 'condition';
      template_id?: number;
      custom_content?: string;
      subject?: string;
      delay_days?: number;
      delay_hours?: number;
      condition_config?: Record<string, any>;
      action_config?: Record<string, any>;
      is_active?: boolean;
    }>;
  }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/api/communication/automation/workflows', workflowData);
    return response.data;
  }

  async updateAutomationWorkflow(id: number, workflowData: {
    name: string;
    description?: string;
    trigger_type: 'member_signup' | 'member_inactive' | 'workout_completed' | 'class_booked' | 'class_missed' | 'custom';
    trigger_config?: Record<string, any>;
    steps: Array<{
      step_order: number;
      action_type: 'send_email' | 'send_sms' | 'send_push' | 'wait' | 'condition';
      template_id?: number;
      custom_content?: string;
      subject?: string;
      delay_days?: number;
      delay_hours?: number;
      condition_config?: Record<string, any>;
      action_config?: Record<string, any>;
      is_active?: boolean;
    }>;
  }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(`/api/communication/automation/workflows/${id}`, workflowData);
    return response.data;
  }

  async activateAutomationWorkflow(id: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/communication/automation/workflows/${id}/activate`);
    return response.data;
  }

  async pauseAutomationWorkflow(id: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/communication/automation/workflows/${id}/pause`);
    return response.data;
  }

  async deleteAutomationWorkflow(id: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/api/communication/automation/workflows/${id}`);
    return response.data;
  }

  async getAutomationWorkflowExecutions(id: number): Promise<any[]> {
    const response: AxiosResponse<any[]> = await this.api.get(`/api/communication/automation/workflows/${id}/executions`);
    return response.data;
  }

  // ==================== ENHANCED REPORTS ====================
  
  async getRevenueReport(startDate?: string, endDate?: string): Promise<any> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const response: AxiosResponse<any> = await this.api.get(`/api/reports/revenue?${params.toString()}`);
    return response.data;
  }

  async getMemberEngagement(startDate?: string, endDate?: string): Promise<any> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const response: AxiosResponse<any> = await this.api.get(`/api/reports/members/engagement?${params.toString()}`);
    return response.data;
  }

  async getMemberRetention(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/reports/members/retention');
    return response.data;
  }

  async getRecentActivity(): Promise<Array<{
    type: string;
    message: string;
    timestamp: string;
  }>> {
    const response: AxiosResponse<any> = await this.api.get('/api/admin/recent-activity');
    return response.data;
  }

  async getRecentMembers(limit: number = 5): Promise<Array<{
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    created_at: string;
    is_active: boolean;
  }>> {
    const response: AxiosResponse<any> = await this.api.get(`/api/members?limit=${limit}&sort=created_at&order=desc`);
    return response.data.members || [];
  }

  async getUpcomingClasses(limit: number = 5): Promise<Array<{
    id: number;
    class_name: string;
    date: string;
    start_time: string;
    end_time: string;
    max_capacity: number;
    current_bookings: number;
  }>> {
    const today = new Date().toISOString().split('T')[0];
    const response: AxiosResponse<any> = await this.api.get(`/api/classes?date=${today}&limit=${limit}`);
    return response.data || [];
  }

  async getDashboardData(): Promise<{
    recent_members: Array<{
      id: number;
      first_name: string;
      last_name: string;
      email: string;
      created_at: string;
      is_active: boolean;
    }>;
    recent_bookings: Array<{
      id: number;
      class_name: string;
      member_name: string;
      date: string;
      start_time: string;
      booked_at: string;
    }>;
    upcoming_classes: Array<{
      id: number;
      class_name: string;
      date: string;
      start_time: string;
      end_time: string;
      max_capacity: number;
      current_bookings: number;
    }>;
  }> {
    const response: AxiosResponse<any> = await this.api.get('/api/admin/dashboard');
    return response.data;
  }

  // Membership Types endpoints
  async getMembershipTypes(): Promise<Array<{
    id: number;
    gym_id: number;
    name: string;
    description?: string;
    price: number;
    class_limit_per_week?: number | null;
    class_limit_per_month?: number | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  }>> {
    const response: AxiosResponse<any> = await this.api.get('/api/membership-types');
    return response.data;
  }

  async getMembershipType(id: number): Promise<{
    id: number;
    gym_id: number;
    name: string;
    description?: string;
    price: number;
    class_limit_per_week?: number | null;
    class_limit_per_month?: number | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  }> {
    const response: AxiosResponse<any> = await this.api.get(`/api/membership-types/${id}`);
    return response.data;
  }

  async createMembershipType(data: {
    name: string;
    description?: string;
    price: number;
    class_limit_per_week?: number | null;
    class_limit_per_month?: number | null;
    is_active?: boolean;
  }): Promise<{
    id: number;
    gym_id: number;
    name: string;
    description?: string;
    price: number;
    class_limit_per_week?: number | null;
    class_limit_per_month?: number | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  }> {
    const response: AxiosResponse<any> = await this.api.post('/api/membership-types', data);
    return response.data;
  }

  async updateMembershipType(id: number, data: {
    name?: string;
    description?: string;
    price?: number;
    class_limit_per_week?: number | null;
    class_limit_per_month?: number | null;
    is_active?: boolean;
  }): Promise<{
    id: number;
    gym_id: number;
    name: string;
    description?: string;
    price: number;
    class_limit_per_week?: number | null;
    class_limit_per_month?: number | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  }> {
    const response: AxiosResponse<any> = await this.api.put(`/api/membership-types/${id}`, data);
    return response.data;
  }

  async deleteMembershipType(id: number): Promise<{ message: string }> {
    const response: AxiosResponse<any> = await this.api.delete(`/api/membership-types/${id}`);
    return response.data;
  }

  // Handbook methods
  async getHandbookSections(): Promise<Array<{
    id: number;
    gym_id: number;
    section_key: string;
    title: string;
    content?: string;
    order_index: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  }>> {
    const response: AxiosResponse<any> = await this.api.get('/api/handbook/sections');
    return response.data;
  }

  async getHandbookSection(key: string): Promise<{
    id: number;
    gym_id: number;
    section_key: string;
    title: string;
    content?: string;
    order_index: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  }> {
    const response: AxiosResponse<any> = await this.api.get(`/api/handbook/sections/${key}`);
    return response.data;
  }

  async updateHandbookSection(key: string, data: {
    title: string;
    content?: string;
    order_index?: number;
    is_active?: boolean;
  }): Promise<{
    id: number;
    gym_id: number;
    section_key: string;
    title: string;
    content?: string;
    order_index: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  }> {
    const response: AxiosResponse<any> = await this.api.put(`/api/handbook/sections/${key}`, data);
    return response.data;
  }

  async deleteHandbookSection(key: string): Promise<{ message: string }> {
    const response: AxiosResponse<any> = await this.api.delete(`/api/handbook/sections/${key}`);
    return response.data;
  }

  async getHandbookSettings(): Promise<{
    id: number | null;
    gym_id: number;
    primary_color: string;
    secondary_color: string;
    logo_url?: string | null;
    header_content?: string | null;
    footer_content?: string | null;
    created_at: string | null;
    updated_at: string | null;
  }> {
    const response: AxiosResponse<any> = await this.api.get('/api/handbook/settings');
    return response.data;
  }

  async updateHandbookSettings(data: {
    primary_color?: string;
    secondary_color?: string;
    logo_url?: string | null;
    header_content?: string | null;
    footer_content?: string | null;
  }): Promise<{
    id: number;
    gym_id: number;
    primary_color: string;
    secondary_color: string;
    logo_url?: string | null;
    header_content?: string | null;
    footer_content?: string | null;
    created_at: string;
    updated_at: string;
  }> {
    const response: AxiosResponse<any> = await this.api.put('/api/handbook/settings', data);
    return response.data;
  }

  // Stripe endpoints - Gym Owner Subscriptions
  async createGymOwnerSubscription(priceId: string): Promise<{
    subscriptionId: string;
    status: string;
    clientSecret: string;
    requiresAction: boolean;
    paymentIntentId?: string;
  }> {
    const response: AxiosResponse<{
      subscriptionId: string;
      status: string;
      clientSecret: string;
      requiresAction: boolean;
      paymentIntentId?: string;
    }> = await this.api.post('/api/stripe/gym-owner/subscribe', { priceId });
    return response.data;
  }

  async getGymOwnerSubscription(): Promise<{
    subscription: {
      id: string;
      status: string;
      currentPeriodEnd: number;
      priceId: string;
      cancelAtPeriodEnd: boolean;
    } | null;
  }> {
    const response: AxiosResponse<{
      subscription: {
        id: string;
        status: string;
        currentPeriodEnd: number;
        priceId: string;
        cancelAtPeriodEnd: boolean;
      } | null;
    }> = await this.api.get('/api/stripe/gym-owner/subscription');
    return response.data;
  }

  async updateGymOwnerSubscription(priceId: string): Promise<{
    subscriptionId: string;
    status: string;
    priceId: string;
  }> {
    const response: AxiosResponse<{
      subscriptionId: string;
      status: string;
      priceId: string;
    }> = await this.api.put('/api/stripe/gym-owner/subscription', { priceId });
    return response.data;
  }

  async cancelGymOwnerSubscription(): Promise<{
    message: string;
    subscriptionId: string;
    status: string;
  }> {
    const response: AxiosResponse<{
      message: string;
      subscriptionId: string;
      status: string;
    }> = await this.api.delete('/api/stripe/gym-owner/subscription');
    return response.data;
  }

  // Stripe endpoints - Connect Account (for member payments)
  async createConnectAccount(): Promise<{
    accountId: string;
    onboardingUrl: string;
    status: string;
  }> {
    const response: AxiosResponse<{
      accountId: string;
      onboardingUrl: string;
      status: string;
    }> = await this.api.post('/api/stripe/connect/create-account');
    return response.data;
  }

  async getConnectAccountStatus(): Promise<{
    accountId: string;
    status: string;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    onboardingComplete: boolean;
  }> {
    const response: AxiosResponse<{
      accountId: string;
      status: string;
      chargesEnabled: boolean;
      payoutsEnabled: boolean;
      onboardingComplete: boolean;
    }> = await this.api.get('/api/stripe/connect/account-status');
    return response.data;
  }

  // Stripe endpoints - Member Payments
  async createMemberPaymentIntent(memberId: number, amount: number, description?: string): Promise<{
    paymentIntentId: string;
    clientSecret: string;
    amount: number;
    currency: string;
    status: string;
  }> {
    const response: AxiosResponse<{
      paymentIntentId: string;
      clientSecret: string;
      amount: number;
      currency: string;
      status: string;
    }> = await this.api.post('/api/stripe/member/payment-intent', {
      memberId,
      amount,
      description
    });
    return response.data;
  }

  async confirmMemberPayment(paymentIntentId: string, paymentMethodId?: string): Promise<{
    paymentIntentId: string;
    status: string;
    clientSecret?: string;
  }> {
    const response: AxiosResponse<{
      paymentIntentId: string;
      status: string;
      clientSecret?: string;
    }> = await this.api.post('/api/stripe/member/confirm-payment', {
      paymentIntentId,
      paymentMethodId
    });
    return response.data;
  }

  // Stripe endpoints - Payment Methods
  async createSetupIntent(): Promise<{
    setupIntentId: string;
    clientSecret: string;
  }> {
    const response: AxiosResponse<{
      setupIntentId: string;
      clientSecret: string;
    }> = await this.api.post('/api/stripe/payment-methods/setup-intent');
    return response.data;
  }

  // CEO endpoints - Gym Management
  async enrollGym(enrollmentData: {
    name: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    ownerFirstName: string;
    ownerLastName: string;
    ownerEmail: string;
    ownerPhone: string;
    businessType: 'gym' | 'crossfit' | 'yoga' | 'martial_arts' | 'dance' | 'other';
    establishedDate: string;
    subscriptionPriceId: string;
  }): Promise<{
    success: boolean;
    message: string;
    gym: {
      name: string;
      email: string;
      id: number;
      subscriptionId?: string;
    };
    owner: {
      email: string;
      tempPassword: string;
      emailSent: boolean;
    };
  }> {
    const response: AxiosResponse<{
      success: boolean;
      message: string;
      gym: {
        name: string;
        email: string;
        id: number;
        subscriptionId?: string;
      };
      owner: {
        email: string;
        tempPassword: string;
        emailSent: boolean;
      };
    }> = await this.api.post('/api/ceo/enroll-gym', enrollmentData);
    return response.data;
  }

  async getCEOGyms(): Promise<any[]> {
    const response: AxiosResponse<any[]> = await this.api.get('/api/ceo/gyms');
    return response.data;
  }

  async getCEOGymDetails(gymId: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/ceo/gyms/${gymId}`);
    return response.data;
  }

  async takeoverGymAccount(gymId: number): Promise<{
    takeoverToken: string;
    gym: {
      id: number;
      name: string;
    };
    owner: {
      id: number;
      name: string;
      email: string;
    };
    loginUrl: string;
  }> {
    const response: AxiosResponse<{
      takeoverToken: string;
      gym: {
        id: number;
        name: string;
      };
      owner: {
        id: number;
        name: string;
        email: string;
      };
      loginUrl: string;
    }> = await this.api.post(`/api/ceo/gyms/${gymId}/takeover`);
    return response.data;
  }

  async updateGymSubscription(gymId: number, priceId: string): Promise<{
    success: boolean;
    subscriptionId: string;
    status: string;
    priceId: string;
  }> {
    const response: AxiosResponse<{
      success: boolean;
      subscriptionId: string;
      status: string;
      priceId: string;
    }> = await this.api.put(`/api/ceo/gyms/${gymId}/subscription`, { priceId });
    return response.data;
  }

  async cancelGymSubscription(gymId: number): Promise<{
    success: boolean;
    message: string;
    subscriptionId: string;
    status: string;
  }> {
    const response: AxiosResponse<{
      success: boolean;
      message: string;
      subscriptionId: string;
      status: string;
    }> = await this.api.delete(`/api/ceo/gyms/${gymId}/subscription`);
    return response.data;
  }

  async decommissionGym(gymId: number, reason?: string, notes?: string): Promise<{
    success: boolean;
    message: string;
    gymId: number;
  }> {
    const response: AxiosResponse<{
      success: boolean;
      message: string;
      gymId: number;
    }> = await this.api.post(`/api/ceo/gyms/${gymId}/decommission`, { reason, notes });
    return response.data;
  }
}

// Create and export singleton instance
export const apiService = new ApiService();
export default apiService; 